import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Building, CreditCard, Hash, Target, X } from 'lucide-react';
import Select from 'react-select';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    customer_type: 'REGULAR',
    inn: '',
    repair_limit: '',
    projects: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const customerTypes = [
    { value: 'BANK', label: 'Банк', icon: '🏦' },
    { value: 'IP', label: 'ИП', icon: '👤' },
    { value: 'SELFEMP', label: 'Самозанятый', icon: '💼' },
    { value: 'REGULAR', label: 'Обычный', icon: '🏢' },
  ];

  useEffect(() => {
    fetchCustomers();
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/settings/projects/');
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Ошибка загрузки проектов для привязки заказчиков');
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/customers/');
      setCustomers(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки заказчиков');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      repair_limit: formData.repair_limit || null,
    };

    try {
      if (editingId) {
        await api.put(`/settings/customers/${editingId}/`, data);
        toast.success('Заказчик обновлён');
      } else {
        await api.post('/settings/customers/', data);
        toast.success('Заказчик создан');
      }
      closeModal();
      fetchCustomers();
    } catch (error) {
      toast.error('Ошибка сохранения заказчика');
    }
  };

  const handleEdit = (customer) => {
    setFormData({
      name: customer.name,
      customer_type: customer.customer_type,
      inn: customer.inn || '',
      repair_limit: customer.repair_limit || '',
      projects: customer.projects || [],
    });
    setEditingId(customer.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить заказчика?')) {
      try {
        await api.delete(`/settings/customers/${id}/`);
        toast.success('Заказчик удалён');
        fetchCustomers();
      } catch (error) {
        toast.error('Ошибка удаления заказчика');
      }
    }
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      customer_type: 'REGULAR',
      inn: '',
      repair_limit: '',
      projects: [],
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: '',
      customer_type: 'REGULAR',
      inn: '',
      repair_limit: '',
      projects: [],
    });
    setEditingId(null);
  };

  const getCustomerTypeInfo = (type) => {
    return customerTypes.find(ct => ct.value === type) || customerTypes[3];
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка заказчиков...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <Building size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Заказчики
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить заказчика
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {customers.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Building size={48} />
            </div>
            <h3>Нет заказчиков</h3>
            <p>Добавьте первого заказчика для начала работы</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>ИНН</th>
                <th>Проекты</th>
                <th>Лимит ремонта</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(customer => {
                const typeInfo = getCustomerTypeInfo(customer.customer_type);
                return (
                  <tr key={customer.id} className={styles.clickableRow} onClick={() => handleEdit(customer)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Building size={16} style={{ color: '#0f766e' }} />
                        <strong>{customer.name}</strong>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{typeInfo.icon}</span>
                        <span>{typeInfo.label}</span>
                      </div>
                    </td>
                    <td>{customer.inn || '—'}</td>
                    <td>{(customer.projects || []).map((id) =>
                      projects.find((project) => String(project.id) === String(id))?.name || `№${id}`
                    ).join(', ') || 'Не привязан'}</td>
                    <td>
                      {customer.repair_limit ? (
                        <span style={{ 
                          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                          color: '#065f46',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8125rem',
                          fontWeight: '600'
                        }}>
                          {customer.repair_limit}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <button
                        className={styles.actionButton}
                        onClick={(event) => { event.stopPropagation(); handleDelete(customer.id); }}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <Building size={20} />
                {editingId ? 'Редактировать заказчика' : 'Добавить заказчика'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <Building size={16} />
                    Название заказчика
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Введите название заказчика"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <CreditCard size={16} />
                    Тип заказчика
                  </label>
                  <div className={styles.select}>
                    <Select
                      options={customerTypes}
                      value={customerTypes.find(ct => ct.value === formData.customer_type)}
                      onChange={(selected) => setFormData({ ...formData, customer_type: selected.value })}
                      placeholder="Выберите тип заказчика"
                      formatOptionLabel={(option) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>{option.icon}</span>
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Hash size={16} />
                    ИНН
                  </label>
                  <input
                    type="text"
                    value={formData.inn}
                    onChange={(e) => setFormData({ ...formData, inn: e.target.value })}
                    placeholder="Введите ИНН"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Target size={16} />
                    Проекты заказчика
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      isSearchable
                      options={projects.map((project) => ({ value: project.id, label: project.name }))}
                      value={projects.filter((project) => formData.projects.some(
                        (id) => String(id) === String(project.id)
                      )).map((project) => ({ value: project.id, label: project.name }))}
                      onChange={(selected) => setFormData({
                        ...formData,
                        projects: (selected || []).map((option) => option.value),
                      })}
                      placeholder="Найдите и привяжите проекты"
                    />
                  </div>
                  <small>Только в выбранных проектах заказчик будет доступен при создании заявки.</small>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Target size={16} />
                    Лимит ремонта
                  </label>
                  <input
                    type="number"
                    value={formData.repair_limit}
                    onChange={(e) => setFormData({ ...formData, repair_limit: e.target.value })}
                    placeholder="Введите лимит ремонта"
                    min="0"
                  />
                </div>

                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Обновить заказчика' : 'Создать заказчика'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
